import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Code2,
  FolderKanban,
  Info,
  Link2,
  Megaphone,
  Package,
  Rocket,
  Settings,
  Shield,
  Users,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import EscrowFundPanel from "@/components/client/EscrowFundPanel";
import CampaignRouteExplainer from "@/components/campaign/CampaignRouteExplainer";
import GuidedTour from "@/components/campaign/GuidedTour";
import RegistrationFormBuilder, { validateRegistrationFields } from "@/components/campaign/RegistrationFormBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  buildProductCreatePayload,
  createProveRequestDraft,
  getClientOnboardingDraftKey,
  validateProveRequestForm,
  validateProveRequestStage,
} from "@/lib/proveRequestForm";
import { resolveProductId } from "@/lib/productId";
import { campaignAPI, clientAPI, integrationAPI, productAPI, projectAPI } from "@/services/api";

const STEPS = [
  { id: 1, title: "Welcome", shortTitle: "Welcome", icon: Rocket, stage: null },
  { id: 2, title: "Project & offer", shortTitle: "Project & offer", icon: Package, stage: "offer" },
  { id: 3, title: "How Agents find customers", shortTitle: "Customer route", icon: Users, stage: "request" },
  { id: 4, title: "How we confirm subscriptions", shortTitle: "Subscription check", icon: Settings, stage: "integration" },
  { id: 5, title: "Price & Agent reward", shortTitle: "Agent reward", icon: Wallet, stage: "economics" },
  { id: 6, title: "Review", shortTitle: "Review", icon: ClipboardCheck, stage: null },
  { id: 7, title: "Fund escrow", shortTitle: "Escrow", icon: Shield, stage: null },
];

const fieldClassName = "mt-1.5 h-11 border-gray-200 focus:border-brand-400";
const helperClassName = "mt-1.5 text-xs leading-5 text-gray-500";
const errorClassName = "mt-1.5 text-sm text-rose-600";

const Annotation = ({ title, children }) => (
  <aside className="rounded-xl border border-brand-200 bg-brand-50 p-4" aria-label={title}>
    <div className="flex items-start gap-3">
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
      <div>
        <h3 className="text-sm font-semibold text-gray-950">{title}</h3>
        <div className="mt-1 text-xs leading-5 text-gray-600">{children}</div>
      </div>
    </div>
  </aside>
);

const readDraft = (draftKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
    const campaignId = saved?.campaignId || null;
    return {
      step: campaignId ? (saved?.form?.requestType === 'direct' && saved?.form?.directVersion === 'v2' && !saved?.registrationFormSaved ? 6 : 7) : Math.min(Math.max(Number(saved?.step) || 1, 1), 6),
      form: createProveRequestDraft(saved?.form || {}),
      registrationFields: Array.isArray(saved?.registrationFields) ? saved.registrationFields : [],
      registrationFormSaved: Boolean(saved?.registrationFormSaved),
      campaignId,
      productId: saved?.productId || null,
      offerName: saved?.offerName || "",
    };
  } catch {
    return { step: 1, form: createProveRequestDraft(), registrationFields: [], registrationFormSaved: false, campaignId: null, productId: null, offerName: "" };
  }
};

const currency = (value) => new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const getOnboardingTourSteps = (step, form) => {
  if (step === 2) return [
    { title: 'Start with a Project', body: 'A Project is your business or product workspace. Put the campaign under the right Project so its results stay together.', anchor: 'project-choice' },
    { title: 'Name your Offer', body: 'Your Offer is the subscription customers can buy. The customer link should lead to your existing signup or checkout flow.', anchor: 'offer-details' },
  ];
  if (step === 3) return [
    { title: 'Choose how customers join', body: 'Simple is a personal introduction and signup on your site. Guided form lets an Agent ask your approved questions. A tracked link can reach many people.', anchor: 'route-choices' },
    { title: 'Compare the routes', body: 'Open the visual guide if you are unsure. All three routes pay only after a subscription is verified.', anchor: 'route-explainer' },
  ];
  if (step === 4) return form.requestType === 'direct' && form.directVersion === 'v2' ? [
    { title: 'Your customer questions', body: 'Choose only safe details. The Agent uses this form; your site handles passwords and finishes account creation.', anchor: 'v2-form' },
    { title: 'Connect registration', body: 'Ask your developer for the secure address where the form should be sent. Your system returns the customer reference.', anchor: 'v2-form' },
    { title: 'Subscription check', body: 'Registration is not a reward. Proovam checks the subscription before an Agent earns.', anchor: 'integration-choice' },
  ] : [
    { title: 'How we confirm subscriptions', body: 'We can check your system, or your system can tell us. Your developer can help connect it.', anchor: 'integration-choice' },
  ];
  if (step === 5) return [
    { title: 'Subscription price', body: 'Enter what one subscription costs your customer.', anchor: 'subscription-price' },
    { title: 'Agent reward', body: 'Choose what an Agent earns only after an eligible subscription is verified.', anchor: 'agent-reward' },
  ];
  if (step === 7) return [{ title: 'Reserve campaign budget', body: 'Funding reserves money for verified Agent rewards. The campaign stays a draft until its connection and publication checks pass.', anchor: 'onboarding-content' }];
  return [{ title: step === 6 ? 'Review before creating' : 'Welcome to Proovam', body: step === 6 ? 'Check your Project, subscription offer, route, and Agent reward before creating the campaign.' : 'Set up a subscription campaign. We only release a reward after the result is verified.', anchor: 'onboarding-content' }];
};

const ClientOnboarding = ({ onComplete, clientId }) => {
  const navigate = useNavigate();
  const draftKey = useMemo(
    () => getClientOnboardingDraftKey(clientId || localStorage.getItem("user_id") || "preview"),
    [clientId],
  );
  const initial = useMemo(() => readDraft(draftKey), [draftKey]);
  const [step, setStep] = useState(initial.step);
  const [form, setForm] = useState(initial.form);
  const [registrationFields, setRegistrationFields] = useState(initial.registrationFields);
  const [registrationFormSaved, setRegistrationFormSaved] = useState(initial.registrationFormSaved);
  const [formSaveError, setFormSaveError] = useState('');
  const [campaignId, setCampaignId] = useState(initial.campaignId);
  const [productId, setProductId] = useState(initial.productId);
  const [offerName, setOfferName] = useState(initial.offerName);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const journeyScrollRef = useRef(null);
  const activeStepRef = useRef(null);

  const current = STEPS[step - 1];
  const progress = Math.round((step / STEPS.length) * 100);

  useEffect(() => {
    if (clientId === "preview") {
      setProjectsLoading(false);
      return undefined;
    }
    let cancelled = false;
    projectAPI.getProjects()
      .then((response) => {
        if (!cancelled) setProjects(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    localStorage.setItem(draftKey, JSON.stringify({
      step,
      form,
      registrationFields,
      registrationFormSaved,
      campaignId,
      productId,
      offerName,
    }));
  }, [campaignId, draftKey, form, offerName, productId, registrationFields, registrationFormSaved, step]);

  useEffect(() => {
    if (!campaignId) return undefined;
    if (!productId) {
      setCampaignId(null);
      setStep(6);
      return undefined;
    }

    let cancelled = false;
    productAPI.getProduct(productId).catch(() => {
      if (cancelled) return;
      setCampaignId(null);
      setProductId(null);
      setStep(6);
      toast.error("We could not verify the saved offer for this account. Please create it again.");
    });
    return () => {
      cancelled = true;
    };
  }, [campaignId, productId]);

  useEffect(() => {
    const container = journeyScrollRef.current;
    const activeStep = activeStepRef.current;
    if (!container || !activeStep) return;
    const left = activeStep.offsetLeft - ((container.clientWidth - activeStep.offsetWidth) / 2);
    if (typeof container.scrollTo === "function") {
      container.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    } else {
      container.scrollLeft = Math.max(0, left);
    }
  }, [step]);

  const update = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const moveTo = (nextStep) => {
    setErrors({});
    setStep(Math.min(Math.max(nextStep, 1), STEPS.length));
  };

  const validateCurrent = () => {
    if (step === 4 && form.requestType === 'direct' && form.directVersion === 'v2') {
      const issue = validateRegistrationFields(registrationFields);
      if (issue) {
        setErrors((previous) => ({ ...previous, registrationFields: issue }));
        toast.error(issue);
        return false;
      }
    }
    if (!current.stage) return true;
    const nextErrors = validateProveRequestStage(form, current.stage);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error(Object.values(nextErrors)[0]);
      return false;
    }
    return true;
  };

  const createOffer = async () => {
    const allErrors = validateProveRequestForm(form);
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      toast.error(Object.values(allErrors)[0]);
      return;
    }
    const needsRegistrationForm = form.requestType === 'direct' && form.directVersion === 'v2';
    if (needsRegistrationForm) {
      const issue = validateRegistrationFields(registrationFields);
      if (issue) {
        setErrors((previous) => ({ ...previous, registrationFields: issue }));
        setStep(4);
        toast.error(issue);
        return;
      }
    }
    setSaving(true);
    try {
      setFormSaveError('');
      let submittedForm = form;
      if (!productId && form.projectMode === "create" && !form.projectId) {
        const projectResponse = await projectAPI.createProject({
          name: form.projectName.trim(),
          description: form.projectDescription?.trim() || null,
          idempotencyKey: form.projectIdempotencyKey,
        });
        const createdProject = projectResponse.data || {};
        const createdProjectId = createdProject.project_id || createdProject.projectId;
        if (!createdProjectId) throw new Error("Project creation did not return a Project ID");
        submittedForm = { ...form, projectId: createdProjectId, projectMode: "existing" };
        setForm(submittedForm);
        setProjects((previous) => previous.some((item) => (item.project_id || item.projectId) === createdProjectId)
          ? previous
          : [createdProject, ...previous]);
        localStorage.setItem(draftKey, JSON.stringify({
          step: 6,
          form: submittedForm,
          registrationFields,
          registrationFormSaved,
          campaignId: null,
          productId: null,
          offerName: "",
        }));
      }
      let nextProductId = productId;
      let nextCampaignId = campaignId;
      if (!nextProductId) {
        const response = await productAPI.createProduct(buildProductCreatePayload(submittedForm));
        const data = response.data || {};
        const product = data.product || data;
        nextProductId = resolveProductId(product);
        nextCampaignId = data.default_prove_request?.campaign_id || product.defaultCampaignId || null;
        if (!nextProductId) throw new Error('The offer response did not include an identifier. Please check your dashboard before retrying.');
        setProductId(nextProductId);
        setCampaignId(nextCampaignId);
      }
      if (needsRegistrationForm && !nextCampaignId) {
        setFormSaveError('Your offer was created, but its campaign could not be found. Open the offer from your dashboard to finish setup.');
        setStep(6);
        return;
      }
      if (needsRegistrationForm && !registrationFormSaved) {
        if (!nextCampaignId) throw new Error('The campaign reference is missing. Open the offer from your dashboard.');
        try {
          await campaignAPI.saveRegistrationForm(nextCampaignId, registrationFields);
          setRegistrationFormSaved(true);
        } catch {
          setFormSaveError('We could not save your customer questions. Your offer is safe; retry saving the form before funding.');
          setStep(6);
          return;
        }
      }

      const persisted = {
        step: 7,
        form: submittedForm,
        registrationFields,
        registrationFormSaved: needsRegistrationForm ? true : registrationFormSaved,
        campaignId: nextCampaignId,
        productId: nextProductId,
        offerName: submittedForm.name,
      };
      localStorage.setItem(draftKey, JSON.stringify(persisted));
      setOfferName(submittedForm.name);
      setCampaignId(nextCampaignId);
      setProductId(nextProductId);
      moveTo(7);

      if (nextProductId && submittedForm.verificationUrl) {
        const baseUrl = new URL(submittedForm.verificationUrl).origin;
        try {
          await integrationAPI.updateIntegration(nextProductId, {
            baseUrl,
            authType: submittedForm.authType === "none" ? null : submittedForm.authType,
            testConnection: false,
            endpoints: [],
          });
        } catch {
          toast.error("Offer created, but the integration configuration still needs to be retried.");
        }
      }

      toast.success("Offer saved. Complete the connection and campaign budget to finish onboarding.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create offer");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateCurrent()) return;
    if (step === 6) {
      const allErrors = validateProveRequestForm(form);
      if (Object.keys(allErrors).length > 0) {
        const firstField = Object.keys(allErrors)[0];
        const firstInvalidStep = {
          name: 2,
          projectName: 2,
          projectId: 2,
          productUrl: 2,
          requestType: 3,
          directVersion: 3,
          integrationMethod: 4,
          verificationUrl: 4,
          registrationUrl: 4,
          authType: 4,
          goalType: 5,
          subscriptionAmount: 5,
          commissionPerGoal: 5,
          userIdentifierType: 5,
        }[firstField] || 2;
        setErrors(allErrors);
        setStep(firstInvalidStep);
        toast.error(Object.values(allErrors)[0]);
        return;
      }
      await createOffer();
      return;
    }
    moveTo(step + 1);
  };

  const handleFinish = async () => {
    if (form.requestType === 'direct' && form.directVersion === 'v2' && !registrationFormSaved) {
      toast.error('Save your customer questions before finishing onboarding.');
      setStep(6);
      return;
    }
    setSaving(true);
    try {
      await clientAPI.completeOnboarding();
      localStorage.removeItem(draftKey);
      onComplete?.();
      navigate("/client/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment is still awaiting confirmation. Try again shortly.");
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div className="space-y-7">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
            <Rocket className="h-7 w-7" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-950">Pay only when marketing works</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
              Tell Proovam what you sell, how a customer subscribes, and what an Agent earns. We only release a reward after the Subscription is verified.
            </p>
          </div>
          <Annotation title="How your setup is organised">
            <p><strong>Project, Offer, and Prove-Request</strong> are three different levels. A Project keeps related work together. Your Offer is the subscription plan customers can buy. Its campaign (Prove-Request) says how Agents introduce customers and what verified result earns a reward.</p>
          </Annotation>
          <div className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {[
              "Choose Direct or Broadcast and the correct workflow version",
              "Connect Subscription verification without sharing customer passwords",
              "Review the exact reward trigger before funding escrow",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 px-4 py-3.5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                <span className="text-sm text-gray-600">{item}</span>
              </div>
            ))}
          </div>
          <Button className="h-11" onClick={() => moveTo(2)}>
            Get started <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      );
    }

    if (step === 2) {
      const selectedProjectId = form.projectId;
      return (
        <div className="space-y-5">
          <div data-tour-id="project-choice"><Annotation title="Why a Project comes first">
            <p>A Project keeps related Offers, Prove-Requests, budgets, and reporting together. Create a focused workspace such as “Consumer subscriptions” rather than repeating the Offer name.</p>
          </Annotation></div>
          {projects.length > 0 ? (
            <div>
              <Label>Project setup</Label>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                {[
                  ["create", "Create a new Project", "Start a separate workspace for this initiative."],
                  ["existing", "Use an existing Project", "Keep this Offer with related work and reporting."],
                ].map(([value, title, detail]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={form.projectMode === value}
                    onClick={() => setForm((previous) => ({
                      ...previous,
                      projectMode: value,
                      projectId: value === "create" ? "" : previous.projectId,
                    }))}
                    className={`rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 ${form.projectMode === value ? "border-brand-400 bg-brand-50" : "border-gray-200 hover:border-brand-200"}`}
                  >
                    <span className="block text-sm font-semibold text-gray-900">{title}</span>
                    <span className="mt-1 block text-xs leading-5 text-gray-500">{detail}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {form.projectMode === "existing" ? (
            <div>
              <Label htmlFor="onboarding-project">Project *</Label>
              <Select value={selectedProjectId} onValueChange={(value) => update("projectId", value)}>
                <SelectTrigger id="onboarding-project" className={fieldClassName} disabled={projectsLoading}>
                  <SelectValue placeholder={projectsLoading ? "Loading Projects…" : "Choose a Project"} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => {
                    const id = project.project_id || project.projectId;
                    return <SelectItem key={id} value={id}>{project.name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              {errors.projectId ? <p className={errorClassName}>{errors.projectId}</p> : null}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 p-4 sm:p-5">
              <div className="flex items-center gap-2">
                <FolderKanban className="h-5 w-5 text-brand-500" />
                <h3 className="text-sm font-semibold text-gray-950">New Project</h3>
              </div>
              <div className="mt-4">
                <Label htmlFor="onboarding-project-name">Project name *</Label>
                <Input id="onboarding-project-name" value={form.projectName} onChange={(event) => update("projectName", event.target.value)} placeholder="e.g. Consumer subscriptions" className={fieldClassName} />
                {errors.projectName ? <p className={errorClassName}>{errors.projectName}</p> : null}
              </div>
              <div className="mt-4">
                <Label htmlFor="onboarding-project-description">Project description</Label>
                <Textarea id="onboarding-project-description" value={form.projectDescription} onChange={(event) => update("projectDescription", event.target.value)} placeholder="What business initiative does this Project support?" rows={2} className="mt-1.5 border-gray-200 focus:border-brand-400" />
              </div>
            </div>
          )}
          <div data-tour-id="offer-details" className="border-t border-gray-100 pt-5">
            <h3 className="text-sm font-semibold text-gray-950">Offer details</h3>
            <p className={helperClassName}>An Offer is the product or plan you want Agents to recommend inside this Project. You can use your website, checkout page, WhatsApp link, or social profile if it is an HTTPS link.</p>
          </div>
          <div>
            <Label htmlFor="onboarding-name">Offer name *</Label>
            <Input id="onboarding-name" value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="e.g. Pro subscription" className={fieldClassName} />
            {errors.name ? <p className={errorClassName}>{errors.name}</p> : null}
          </div>
          <div>
            <Label htmlFor="onboarding-description">Description</Label>
            <Textarea id="onboarding-description" value={form.description} onChange={(event) => update("description", event.target.value)} placeholder="What are customers subscribing to?" rows={4} className="mt-1.5 border-gray-200 focus:border-brand-400" />
          </div>
          <div>
            <Label htmlFor="onboarding-product-url">Where can a customer subscribe? *</Label>
            <Input id="onboarding-product-url" aria-label="Product or signup URL" value={form.productUrl} onChange={(event) => update("productUrl", event.target.value)} placeholder="https://yourapp.com/signup" className={fieldClassName} />
            <p className={helperClassName}>Paste the link customers use to start or ask about a Subscription. It can be a website, checkout page, WhatsApp link, or social profile. Use the full HTTPS link without extra tracking text.</p>
            {errors.productUrl ? <p className={errorClassName}>{errors.productUrl}</p> : null}
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="space-y-6">
          <Annotation title="Choose how you want Agents to find customers">
            <p><strong>One-to-one</strong> is for a personal introduction: one Agent brings one customer. <strong>Broadcast</strong> is for sharing one tracked campaign link with many people. If customers already sign up on your website, choose the simple website signup option.</p>
          </Annotation>
          <div data-tour-id="route-explainer"><CampaignRouteExplainer /></div>
          <div data-tour-id="route-choices" className="grid gap-4 md:grid-cols-2">
            {[
              { value: "direct", title: "Meet customers one at a time", description: "An Agent personally introduces each customer. Choose Simple or Guided form below.", badge: 'Direct', icon: Users },
              { value: "broadcast", title: "Share a tracked link", description: "An Agent shares a campaign link with many people. Your site handles signup.", badge: 'Broadcast', icon: Megaphone },
            ].map((choice) => (
              <button
                key={choice.value}
                type="button"
                aria-label={choice.value === "direct" ? "Direct Proove" : "Broadcast Proove"}
                aria-pressed={form.requestType === choice.value}
                onClick={() => setForm((previous) => ({
                  ...previous,
                  requestType: choice.value,
                  directVersion: choice.value === "broadcast" ? "" : previous.directVersion,
                  integrationMethod: choice.value === "broadcast" ? "webhook" : "api",
                  referralEnabled: choice.value === "broadcast",
                  audiencePayEnabled: choice.value === "broadcast" ? previous.audiencePayEnabled : false,
                }))}
                className={`rounded-xl border p-5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 ${form.requestType === choice.value ? "border-brand-400 bg-brand-50 ring-1 ring-brand-100" : "border-gray-200 bg-white hover:border-brand-200"}`}
              >
                <choice.icon className="h-6 w-6 text-brand-500" />
                <span className="mt-3 block text-xs font-semibold uppercase tracking-wide text-brand-600">{choice.badge}</span>
                <span className="mt-4 block text-base font-semibold text-gray-950">{choice.title}</span>
                <span className="mt-1.5 block text-sm leading-6 text-gray-500">{choice.description}</span>
              </button>
            ))}
          </div>
          {form.requestType === "direct" ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">How should this one-to-one introduction work?</h3>
              <p className={helperClassName}>Choose where the customer details are entered. A reward is earned only after the subscription is verified.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {[
                  ["v1", "Customer signs up on your site", "The Agent records the introduction and shares your signup link. Proovam checks the subscription. Simple signup · V1"],
                  ["v2", "Agent uses your customer form", "The Agent asks your approved questions. Your system creates the account, then Proovam checks the subscription. Guided form · V2"],
                ].map(([value, title, description]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={form.directVersion === value}
                    onClick={() => update("directVersion", value)}
                    className={`rounded-xl border p-4 text-left transition ${form.directVersion === value ? "border-brand-400 bg-brand-50" : "border-gray-200 bg-white hover:border-brand-200"}`}
                  >
                    <span className="block text-sm font-semibold text-gray-900">{title}</span>
                    <span className="mt-1 block text-xs leading-5 text-gray-500">{description}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {errors.requestType || errors.directVersion ? <p className={errorClassName}>{errors.requestType || errors.directVersion}</p> : null}
        </div>
      );
    }

    if (step === 4) {
      const needsRegistration = form.requestType === "direct" && form.directVersion === "v2";
      return (
        <div className="space-y-5">
          <Annotation title="How will we know a customer subscribed?">
            <div className="space-y-2">
              <p>We can check your system for a subscription, or your system can tell us when one starts. Either way, an Agent earns only after the subscription is verified.</p>
              <p>Your developer can provide the secure address. The customer signup link is separate: it is the page a customer visits, while this connection lets two systems confirm subscription status.</p>
              <details className="mt-2"><summary className="cursor-pointer font-medium">For your developer: how each connection works</summary><p><strong>Secure API lookup:</strong> Proovam requests the latest subscription record from your system. <strong>Webhook notification:</strong> your system sends Proovam an update when subscription status changes.</p></details>
            </div>
          </Annotation>
          <div data-tour-id="integration-choice">
            <Label>Choose how the subscription is confirmed</Label>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {[
                ["api", "We check your subscription records", "Proovam asks your system for the latest status when verification is needed.", Code2],
                ["webhook", "Your system sends subscription updates", "Your system notifies Proovam after activation or another status change.", Link2],
              ].map(([value, title, description, Icon]) => (
                <button key={value} type="button" aria-pressed={form.integrationMethod === value} onClick={() => update("integrationMethod", value)} className={`rounded-xl border p-4 text-left ${form.integrationMethod === value ? "border-brand-400 bg-brand-50" : "border-gray-200"}`}>
                  <Icon className="h-5 w-5 text-brand-500" />
                  <span className="mt-2 block text-sm font-semibold text-gray-900">{title}</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">{description}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="onboarding-verification-url">{form.integrationMethod === "webhook" ? "Where your system sends subscription updates *" : "Where Proovam checks subscription status *"}</Label>
            <Input id="onboarding-verification-url" value={form.verificationUrl} onChange={(event) => update("verificationUrl", event.target.value)} placeholder="https://api.yourapp.com/proovam/subscription" className={fieldClassName} />
            <p className={helperClassName}>{form.integrationMethod === "webhook" ? "Ask your developer for the secure address that receives subscription updates." : "Ask your developer for the secure address Proovam can use to check a customer's subscription."} This is not the customer signup link.</p>
            {errors.verificationUrl ? <p className={errorClassName}>{errors.verificationUrl}</p> : null}
          </div>
          {needsRegistration ? (
            <section data-tour-id="v2-form" className="space-y-4 rounded-xl border border-brand-200 p-4">
              <div>
                <h3 className="text-base font-semibold text-gray-950">Your customer questions</h3>
                <p className={helperClassName}>Choose the safe details an Agent asks for during registration. Your own system creates the account and keeps passwords on your site.</p>
              </div>
              <RegistrationFormBuilder draftMode initialFields={registrationFields} onDraftChange={(fields) => { setRegistrationFields(fields); setErrors((previous) => ({ ...previous, registrationFields: undefined })); }} />
              {registrationFields.length > 0 && <p className="text-xs text-emerald-700">{registrationFields.length} question{registrationFields.length === 1 ? '' : 's'} ready to save with your campaign.</p>}
              {errors.registrationFields && <p className={errorClassName}>{errors.registrationFields}</p>}
              <details className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                <summary className="cursor-pointer font-medium">For your developer: registration connection</summary>
                <Label htmlFor="onboarding-registration-url" className="mt-3 block">Where to send new customer details *</Label>
                <Input id="onboarding-registration-url" value={form.registrationUrl} onChange={(event) => update("registrationUrl", event.target.value)} placeholder="https://api.yourapp.com/proovam/register" className={fieldClassName} />
                <p className={helperClassName}>This secure address must return a stable customer reference. Do not add tracking text to the URL.</p>
                {errors.registrationUrl ? <p className={errorClassName}>{errors.registrationUrl}</p> : null}
              </details>
            </section>
          ) : null}
          <details className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <summary className="cursor-pointer text-sm font-semibold text-gray-900">For your developer: security and connection test</summary>
            <div className="mt-4 space-y-4">
              <div>
                <Label htmlFor="onboarding-auth-type">Connection security</Label>
                <Select value={form.authType} onValueChange={(value) => update("authType", value)}>
                  <SelectTrigger id="onboarding-auth-type" className={fieldClassName}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No authentication</SelectItem>
                    <SelectItem value="jwt">Bearer token</SelectItem>
                    <SelectItem value="api_key">API key</SelectItem>
                  </SelectContent>
                </Select>
                <p className={helperClassName}>Credentials are added securely after the offer exists; they are never stored in this draft.</p>
              </div>
              <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-amber-900">Connection test available after creation</p>
                  <p className="mt-0.5 text-xs text-amber-700">Create the offer first, then test this secure connection before publishing the campaign.</p>
                </div>
                <Button variant="outline" disabled>Test after creation</Button>
              </div>
            </div>
          </details>
        </div>
      );
    }

    if (step === 5) {
      return (
        <div className="space-y-5">
          <div>
            <Label htmlFor="onboarding-goal">Goal</Label>
            <Input id="onboarding-goal" value="Subscription" readOnly className={`${fieldClassName} bg-gray-50`} />
            <p className={helperClassName}>Goal-plugin V1 supports Subscription only. Additional goal plugins will be added independently.</p>
          </div>
          <Annotation title="What these numbers mean">
            <p>Enter what one Subscription costs and the amount an Agent should earn for each verified Subscription. You can review and edit these values on the next screen before creating the Offer.</p>
          </Annotation>
          <div className="grid gap-4 sm:grid-cols-2">
            <div data-tour-id="subscription-price">
              <Label htmlFor="onboarding-subscription-amount">Subscription amount (NGN) *</Label>
              <Input id="onboarding-subscription-amount" type="number" min="1" value={form.subscriptionAmount} onChange={(event) => update("subscriptionAmount", event.target.value)} className={fieldClassName} />
              {errors.subscriptionAmount ? <p className={errorClassName}>{errors.subscriptionAmount}</p> : null}
            </div>
            <div data-tour-id="agent-reward">
              <Label htmlFor="onboarding-commission">Commission per verified Subscription (NGN) *</Label>
              <Input id="onboarding-commission" type="number" min="1" value={form.commissionPerGoal} onChange={(event) => update("commissionPerGoal", event.target.value)} className={fieldClassName} />
              {errors.commissionPerGoal ? <p className={errorClassName}>{errors.commissionPerGoal}</p> : null}
            </div>
          </div>
          <div>
            <Label htmlFor="onboarding-identifier">Customer identifier</Label>
            <Select value={form.userIdentifierType} onValueChange={(value) => update("userIdentifierType", value)}>
              <SelectTrigger id="onboarding-identifier" className={fieldClassName}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone number</SelectItem>
                <SelectItem value="email">Email address</SelectItem>
                <SelectItem value="custom">External customer ID</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.requestType === "broadcast" ? (
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Performance-triggered media fee</p>
                  <p className={helperClassName}>Allow a negotiated distribution fee only after the agreed trigger is reached.</p>
                </div>
                <Switch checked={form.audiencePayEnabled} onCheckedChange={(checked) => update("audiencePayEnabled", checked)} aria-label="Enable audience media fee" />
              </div>
            </div>
          ) : null}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            Example: 10 verified subscriptions × {currency(form.commissionPerGoal)} = {currency(Number(form.commissionPerGoal) * 10)} in Agent commission.
          </div>
        </div>
      );
    }

    if (step === 6) {
      const selectedProject = projects.find((project) => (project.project_id || project.projectId) === form.projectId);
      const rows = [
        ["Project", selectedProject?.name || form.projectName],
        ["Offer", form.name],
        ["Request", form.requestType === "direct" ? `Direct ${form.directVersion?.toUpperCase()}` : "Broadcast"],
        ["Goal plugin", "Subscription · V1"],
        ["Evidence", `${form.integrationMethod.toUpperCase()} · ${form.verificationUrl}`],
        ["Customer identifier", form.userIdentifierType],
        ["Subscription amount", currency(form.subscriptionAmount)],
        ["Agent commission", currency(form.commissionPerGoal)],
      ];
      return (
        <div className="space-y-5">
          <div className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {rows.map(([label, value]) => (
              <div key={label} className="grid gap-1 px-4 py-3 sm:grid-cols-[180px_1fr]">
                <span className="text-sm text-gray-500">{label}</span>
                <span className="break-all text-sm font-medium capitalize text-gray-900">{value || "Not set"}</span>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 text-sm leading-6 text-gray-700">
            Creating this offer also creates a draft Prove-Request. It remains hidden from BRMs until escrow is funded and required integration contracts pass their tests.
          </div>
          {form.requestType === 'direct' && form.directVersion === 'v2' && <p className="text-sm text-gray-700">Your {registrationFields.length} approved customer question{registrationFields.length === 1 ? '' : 's'} will be saved to this campaign before you fund it.</p>}
          {formSaveError && <p role="alert" className={errorClassName}>{formSaveError}</p>}
        </div>
      );
    }

    if (!campaignId) {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          <p>The offer exists, but the escrow reference is unavailable. Finish onboarding, then open the offer to fund its Prove-Request.</p>
          <Button className="mt-4" onClick={handleFinish}>Go to dashboard</Button>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <EscrowFundPanel campaignId={campaignId} />
        <Button className="h-11 w-full" onClick={handleFinish} disabled={saving}>
          {saving ? "Confirming escrow…" : "Complete onboarding"}
        </Button>
        <p className="text-center text-xs text-gray-500">{offerName} remains a draft until payment and connection readiness are confirmed.</p>
      </div>
    );
  };

  const guidanceByStep = {
    1: [
      { title: "Create a Project", detail: "Group related Offers, requests, budgets, and reporting." },
      { title: "Define one Offer", detail: "Name the product or subscription plan being promoted." },
      { title: "Launch a Prove-Request", detail: "Set the workflow, evidence, commission, and escrow." },
    ],
    2: [
      { title: "Project", detail: "The long-lived workspace for a business initiative." },
      { title: "Offer", detail: "The product, service, or plan customers buy." },
      { title: "Default request", detail: "Created automatically under this Offer as a draft." },
    ],
    4: [
      { title: "Share the signup link", detail: "Customers use this link to start subscribing." },
      { title: "Connect the subscription check", detail: "Your developer supplies the secure system address." },
      { title: "Test before launch", detail: "Agents see the campaign after the required checks pass." },
    ],
  };
  const nextItems = guidanceByStep[step] || [
    { title: "Define the request", detail: "Offer, Direct or Broadcast, and version" },
    { title: "Connect Subscription evidence", detail: "HTTPS contract and customer identifier" },
    { title: "Set verified economics", detail: "Subscription value and Agent commission" },
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50">
      <section className="border-b border-gray-200 bg-white" aria-label="Onboarding journey">
        <div ref={journeyScrollRef} className="mx-auto max-w-5xl overflow-x-auto px-4 py-4 sm:px-6">
          <div className="relative min-w-[680px]">
            <progress className="sr-only" aria-label="Setup progress" aria-valuemax={STEPS.length} max={STEPS.length} value={step}>
              Step {step} of {STEPS.length}: {current.title}
            </progress>
            <nav className="relative flex items-center gap-2" aria-label="Onboarding steps">
              {STEPS.map((item) => {
                const complete = item.id < step;
                const active = item.id === step;
                return (
                  <button
                    key={item.id}
                    ref={active ? activeStepRef : undefined}
                    type="button"
                    disabled={item.id > step || Boolean(campaignId)}
                    onClick={() => item.id <= step && !campaignId && moveTo(item.id)}
                    className={`group flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-default ${active ? 'bg-brand-50' : ''}`}
                    aria-current={active ? "step" : undefined}
                  >
                    <span className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition ${complete ? "border-emerald-300 bg-emerald-50 text-emerald-600" : active ? "border-brand-500 bg-brand-500 text-white" : "border-gray-200 bg-white text-gray-500"}`}>
                      {complete ? <Check className="h-4 w-4" /> : item.id}
                    </span>
                    <span title={item.shortTitle} className={`text-xs font-medium leading-tight ${active ? "text-gray-950" : complete ? "text-emerald-700" : "text-gray-500"}`}>{item.shortTitle}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-5xl gap-5 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-xs">
          <div className="border-b border-gray-100 px-5 py-5 sm:px-7">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-500">Step {step} of {STEPS.length}</p>
            <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="mt-1 text-2xl font-semibold text-gray-950">{current.title}</h1><GuidedTour accountId={clientId || 'preview'} tourId={`onboarding-step-${step}`} steps={getOnboardingTourSteps(step, form)} /></div>
            <p className="mt-1 text-sm text-gray-500">Complete this stage to prepare your first subscription campaign.</p>
          </div>

          <AnimatePresence mode="wait">
            <motion.div data-tour-id="onboarding-content" key={step} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.18 }} className="p-5 sm:p-7">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          {step > 1 && step < 7 ? (
            <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
              <div className="inline-flex items-center gap-2 text-xs font-medium text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> Autosaved
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => moveTo(step - 1)} disabled={saving || Boolean(campaignId)}>
                  <ChevronLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={step === 6 && productId && !campaignId ? () => navigate('/client/dashboard') : handleNext} disabled={saving}>
                  {step === 6 ? (saving ? "Saving offer…" : productId && !campaignId ? 'Open dashboard' : formSaveError ? 'Retry saving form' : 'Create offer') : "Continue"}
                  {step < 6 ? <ChevronRight className="ml-2 h-4 w-4" /> : null}
                </Button>
              </div>
            </div>
          ) : null}
        </section>

        <aside className="space-y-4">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs">
            <h2 className="text-base font-semibold text-gray-950">{step === 1 ? "How Proovam is organised" : step === 2 ? "Project or Offer?" : "What happens next"}</h2>
            <div className="mt-4 divide-y divide-gray-100">
              {nextItems.map((item, index) => (
                <div key={item.title} className="flex gap-3 py-4 first:pt-0 last:pb-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-xs font-semibold text-brand-500">{index + 1}</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
            <p className="text-sm font-semibold text-gray-900">Verified subscriptions · Simple signup</p>
            <p className="mt-2 text-xs leading-5 text-gray-600">This campaign rewards an Agent only after the customer's subscription is verified.</p>
          </section>
          <div className="px-2 text-xs text-gray-500">{progress}% of setup reached</div>
        </aside>
      </div>
    </div>
  );
};

export default ClientOnboarding;
